// Futuria · Inteligencia para Universidades (BI B2B).
// Cuántas veces se mencionó tu universidad, qué carreras interesan a los alumnos,
// demanda insatisfecha y recomendaciones de plan de estudios por IA.
// Consume Django /api/v1/futuria/bi/* (proxy /django-api). Tokens semánticos.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  GraduationCap, TrendingUp, TrendingDown, Minus, Loader2, AlertTriangle,
  Sparkles, Target, BarChart3, MapPin, Building2, Lightbulb,
} from 'lucide-react';

import { getUniversidades, getOverview, getCurriculum } from '../api';
import type { UniItem, Overview, Curriculum, CarreraInteres } from '../types';

const num = (n: number) => new Intl.NumberFormat('es-MX').format(n || 0);
const interesOf = (c: CarreraInteres) => c.interes ?? c.n ?? 0;
const cleanMd = (s: string) =>
  (s || '').replace(/\*\*(.*?)\*\*/g, '$1').replace(/^#{1,6}\s*/gm, '').replace(/^\s*[-*]\s+/gm, '• ').trim();

export function UniversityBIPage() {
  const [unis, setUnis] = useState<UniItem[]>([]);
  const [sel, setSel] = useState<string>('');
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getUniversidades()
      .then((u) => { setUnis(u); if (u[0]) setSel(u[0].siglas); })
      .catch((e) => setError(String(e.message || e)));
  }, []);

  useEffect(() => {
    if (!sel) return;
    setLoading(true);
    getOverview(sel)
      .then(setData)
      .catch((e) => setError(String(e.message || e)))
      .finally(() => setLoading(false));
  }, [sel]);

  const uni = data?.universidad;
  const maxTop = useMemo(() => Math.max(1, ...(data?.top_carreras || []).map((c) => interesOf(c))), [data]);
  const maxRank = useMemo(() => Math.max(1, ...(data?.ranking_universidades || []).map((r) => r.menciones)), [data]);

  if (error && !data) {
    return (
      <div className="m-6 rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        <AlertTriangle className="inline mr-2 text-red-500" size={16} /> No se pudo cargar: {error}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <header className="flex items-start gap-3">
        <GraduationCap className="text-primary mt-1" size={28} />
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-foreground">Inteligencia para Universidades</h1>
          <p className="text-sm text-muted-foreground">
            Qué estudian los que preguntan, cuánto te mencionan y dónde está la oportunidad.
          </p>
        </div>
        <select
          value={sel}
          onChange={(e) => setSel(e.target.value)}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary"
        >
          {unis.map((u) => (
            <option key={u.siglas} value={u.siglas}>{u.nombre} ({u.programas})</option>
          ))}
        </select>
      </header>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <Loader2 className="animate-spin" size={16} /> Cargando inteligencia…
        </div>
      )}

      {data && uni && (
        <>
          {/* KPIs de la universidad */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Menciones de tu universidad" value={num(uni.menciones)} icon={<Building2 size={16} />} />
            <Kpi label="Ranking nacional" value={uni.rank ? `#${uni.rank}` : '—'} icon={<BarChart3 size={16} />} />
            <Kpi
              label="Tendencia (30 días)"
              value={uni.tendencia === 'sube' ? 'Subiendo' : uni.tendencia === 'baja' ? 'Bajando' : 'Estable'}
              icon={uni.tendencia === 'sube' ? <TrendingUp size={16} className="text-emerald-500" />
                : uni.tendencia === 'baja' ? <TrendingDown size={16} className="text-red-500" /> : <Minus size={16} />}
            />
            <Kpi label="Interés total (base)" value={num(data.total_interes)}
                 sub={`${num(data.interes_real)} reales · ${num(data.interes_proyectado)} proyección`}
                 icon={<Target size={16} />} />
          </div>

          {/* Demanda insatisfecha — la oportunidad (destacada) */}
          {uni.demanda_insatisfecha.length > 0 && (
            <Section title="Demanda insatisfecha — oportunidad de nuevos programas" icon={<Lightbulb size={16} className="text-primary" />}>
              <p className="mb-3 text-xs text-muted-foreground">
                Carreras que los alumnos quieren y que <b className="text-foreground">{uni.nombre}</b> aún no ofrece.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {uni.demanda_insatisfecha.map((c, i) => (
                  <div key={i} className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-foreground font-medium">{c.carrera}</span>
                      <span className="text-primary font-semibold">{num(interesOf(c))}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">consultas de alumnos</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Recomendaciones de plan de estudios (IA) */}
          <CurriculumPanel siglas={sel} nombre={uni.nombre} />

          {/* Carreras de mayor interés (global) */}
          <Section title="Carreras de mayor interés (todos los alumnos)" icon={<BarChart3 size={16} />}>
            <div className="space-y-1.5">
              {(data.top_carreras || []).slice(0, 12).map((c, i) => (
                <BarRow key={i} label={c.carrera} value={interesOf(c)} max={maxTop} />
              ))}
            </div>
          </Section>

          {/* Carreras que ofreces + su interés */}
          {uni.carreras_que_ofrece.length > 0 && (
            <Section title={`Tus carreras y su demanda`} icon={<GraduationCap size={16} />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {uni.carreras_que_ofrece.slice(0, 16).map((c, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 text-sm">
                    <span className="text-foreground">{c.carrera}</span>
                    <span className="text-muted-foreground">{num(interesOf(c))} consultas</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Ranking de universidades */}
          <Section title="Ranking de menciones (todas las universidades)" icon={<Building2 size={16} />}>
            <div className="space-y-1.5">
              {(data.ranking_universidades || []).slice(0, 12).map((r) => (
                <BarRow
                  key={r.universidad}
                  label={`#${r.rank}  ${r.universidad}`}
                  value={r.menciones} max={maxRank}
                  highlight={r.universidad === sel}
                />
              ))}
            </div>
          </Section>

          {/* Perfil por región */}
          {data.perfil_region.length > 0 && (
            <Section title="Perfil del interesado (región)" icon={<MapPin size={16} />}>
              <div className="flex flex-wrap gap-2">
                {data.perfil_region.map((p, i) => (
                  <span key={i} className="rounded-full bg-muted px-3 py-1 text-xs text-foreground capitalize">
                    {p.region}: <b>{num(p.n)}</b>
                  </span>
                ))}
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">{icon}{label}</div>
      <div className="mt-1 text-lg font-semibold text-foreground">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground/80 mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">{icon}{title}</h2>
      {children}
    </section>
  );
}

function BarRow({ label, value, max, highlight }: { label: string; value: number; max: number; highlight?: boolean }) {
  const pct = Math.max(3, Math.round((value / max) * 100));
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className={`w-56 shrink-0 truncate ${highlight ? 'text-primary font-semibold' : 'text-foreground'}`}>{label}</span>
      <div className="flex-1 h-4 rounded bg-muted overflow-hidden">
        <div className={`h-full ${highlight ? 'bg-primary' : 'bg-primary/60'}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-14 text-right text-muted-foreground">{num(value)}</span>
    </div>
  );
}

function CurriculumPanel({ siglas, nombre }: { siglas: string; nombre: string }) {
  const [data, setData] = useState<Curriculum | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset al cambiar de universidad.
  useEffect(() => { setData(null); setErr(null); }, [siglas]);

  const generate = async () => {
    setBusy(true); setErr(null);
    try {
      setData(await getCurriculum(siglas));
    } catch {
      setErr('La IA tardó o no está disponible. Intenta de nuevo en unos segundos.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-lg border border-primary/30 bg-primary/[0.03] p-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Sparkles size={16} className="text-primary" /> Recomendaciones de plan de estudios (IA)
        </h2>
        <button
          onClick={generate}
          disabled={busy}
          className="rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
        >
          {busy ? <Loader2 className="animate-spin" size={16} /> : data ? 'Regenerar' : 'Generar análisis'}
        </button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        La IA cruza el interés real de los alumnos + tu oferta actual + realidad laboral (IMCO) +
        tendencias de universidades top del primer mundo, para <b className="text-foreground">{nombre}</b>.
      </p>
      {busy && <p className="mt-3 text-xs text-muted-foreground">Analizando (puede tardar ~1 min)…</p>}
      {err && <p className="mt-3 text-xs text-muted-foreground">{err}</p>}
      {data?.recomendaciones && (
        <p className="mt-3 whitespace-pre-wrap text-sm text-foreground leading-relaxed">
          {cleanMd(data.recomendaciones)}
        </p>
      )}
    </section>
  );
}
